import { GridColumn } from '@progress/kendo-react-grid'
import classnames from 'classnames'
import { HeaderComponent } from 'components/header/component'
import { RasaContext } from 'context'
import { BaseClientEntity } from 'generic/baseClientEntity'
import { Dataset } from 'generic/dataset'
import { EditableDropdownCell, EditCellProps } from 'generic/editCell'
import * as GenericRedux from 'generic/genericRedux'
import { PagingSource, RasaDataGrid } from 'generic/rasaDataGrid'
import React, { Component } from 'react'
import { Button } from 'reactstrap'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { ModalSize } from 'shared/modals/constants'
import { Loading } from '../../loading'
import { AddAIImagePreferenceModal } from './modal'
import { AIImageSettingsCell } from './settingsCell'
import './styles.css'

interface UserAiImagePreference {
  id: string
  name: string
  options: {
    style: string
    quality: string
    aspectRatio: string
    colorScheme: string
    mood: string
    complexity: string
    artStyle: string
    lighting: string
  }
  is_default: boolean
  created: string
}

type AIImagePreferencesProps = GenericRedux.AllComponentPropsWithModal<UserAiImagePreference>

interface AIImagePreferencesState {
  communityId: string
  editId: string
  editName: string
  editOptions: any
  editIsDefault: boolean
  forceReload: boolean
  preferences: UserAiImagePreference[]
  isSaveButtonDisabled: boolean
  loading: boolean
}

class AIImagePreferencesComponent extends Component<AIImagePreferencesProps, AIImagePreferencesState> {
  public static contextType = RasaContext
  private entityName: string = 'userAiPreference'
  private sharedStore: SharedStore

  constructor(props: AIImagePreferencesProps) {
    super(props)
    this.state = {
      communityId: '',
      editId: null,
      editName: '',
      editOptions: null,
      editIsDefault: false,
      forceReload: false,
      preferences: [],
      isSaveButtonDisabled: true,
      loading: true,
    }
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
    ]).then(([activeCommunity]) => {
      this.getImagePreferences(activeCommunity.communityId)
      .then((preferences) => {
        this.setState({
          communityId: activeCommunity.communityId,
          preferences,
          loading: false,
        })
      })
    })
  }

  public render() {
    return (
      this.state.loading ? <Loading size="64" /> :
      <div className="ai-image-preferences">
        <div className="header-container">
          <AddAIImagePreferenceModal
            data={this.props.modals}
            closeModal={this.props.closeModal}
            createOrUpdate={this.createOrUpdate}
            saveModal={this.props.saveModal}
            updateModal={this.props.updateModal}
            name={this.state.editName}
            options={this.state.editOptions}
            isDefault={this.state.editIsDefault}
            size={ModalSize.Medium} />
          <HeaderComponent
            title={'Preferences'}
            subTitle={'AI Image'}
          />
          <div className="section-right-side">
            <div className="section-button-container">
              <div className={classnames('add-sections-button-text-section')}>
                <Button
                  className={classnames('add-sections-button')}
                  onClick={() =>
                    this.props.openModal(AddAIImagePreferenceModal.key, null)}>
                  <span className="sections-button-text">
                    Add New Preference
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div>
          <RasaDataGrid
            // Rasa Properties for entity, datasetName etc
            entityName="userAiPreference"
            datasetName="userAiPreferences"
            datasetParams={[
              { param: 'is_active', value: 1 },
              { param: 'preference_type', value: 'image' },
            ]}
            editDefaultState={true}
            forceReload={this.state.forceReload}
            // events
            sortable={true}
            pageable={false}
            pagingSource={PagingSource.local}
            data={[]}>
            <GridColumn title="Name" field="name" width={200} />
            <GridColumn title="Settings" field="options" width={500} cell={(props) => <AIImageSettingsCell dataItem={props.dataItem} />} />
            <GridColumn title="Default" field="is_default" width={120}
              cell={(props) => {
                return <td><div>{props.dataItem.is_default ? 'Yes' : 'No'}</div></td>
              }} />
            <GridColumn width={100} cell={this.PreferenceEditCell} />
          </RasaDataGrid>
        </div>
      </div>
    )
  }

  protected PreferenceEditCell = (props: EditCellProps) => {
    return <EditableDropdownCell {...props} canEdit={true} onEdit={this.editClick}
            onArchive={this.onArchive} canArchive={true} />
  }

  private editClick = (dataItem: any) => {
    let parsedOptions = null
    try {
      parsedOptions = typeof dataItem.options === 'string' ? JSON.parse(dataItem.options) : dataItem.options
    } catch (e) {
      parsedOptions = null
    }

    this.setState({
      editId: dataItem.id,
      editName: dataItem.name,
      editOptions: parsedOptions,
      editIsDefault: dataItem.is_default,
    }, () => {
      this.props.openModal(AddAIImagePreferenceModal.key, dataItem)
    })
  }

  private onArchive = (props: any, event: any) => {
    this.context.entityMetadata.getEntityObject(this.entityName).then(
      (entityObject: BaseClientEntity) => {
        entityObject.setRecordId(this.state.communityId, props.dataItem.id)
        entityObject.archive()
        .then(() => {
          this.setState({
            forceReload: true,
          }, () => {
            this.getImagePreferences(this.state.communityId)
            .then((preferences) => {
              this.setState({
                preferences,
                forceReload: false,
              })
            })
          })
        })
      })
  }

  private createOrUpdate = (payload: any) => {
    return this.context.entityMetadata
    .getEntityObject(this.entityName, this.state.communityId, null)
    .then((preferenceEntityObject: BaseClientEntity) => {
      preferenceEntityObject.setRecordId(this.state.communityId, this.state.editId ? this.state.editId : null)
      preferenceEntityObject.data.name = payload.name
      preferenceEntityObject.data.options = JSON.stringify(payload.options)
      preferenceEntityObject.data.is_default = payload.is_default
      preferenceEntityObject.data.preference_type = 'image'
      preferenceEntityObject.data.community_identifier = this.state.communityId
      return preferenceEntityObject.save()
        .then(() => {
          this.props.resetModal(AddAIImagePreferenceModal.key)
          this.setState({
            forceReload: true,
            editId: null,
            editName: '',
            editOptions: null,
            editIsDefault: false,
          }, () => {
            this.setState({ forceReload: false })
          })
          this.getImagePreferences(this.state.communityId)
          .then((preferences) => {
            this.setState({
              preferences,
            })
          })
        })
    })
  }

  private getImagePreferences = (community) => {
    return new Dataset()
    .loadCommunityDataset('userAiPreferences', community, [
      {param: 'is_active', value: 1}, 
      {param: 'preference_type', value: 'image'}
    ])
    .then((response) => {
      return response[0] || []
    })
  }
}

export const AIImagePreferencesSettings = GenericRedux.registerNewComponentWithModals<UserAiImagePreference>(
  AIImagePreferencesComponent,
  'user_ai_image_preferences',
  [AddAIImagePreferenceModal.key],
)